// Iter 9.50+ R17 - Design preview screens for upcoming features
// (Routines, Inspo, Preferences, Notifications, Settings).
//
// These are static design previews so the team can review look + flow before
// real backend wiring. Each flow lives at /design/<flow>?s=<state>.
import { useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import PreferenceCardDesigns from "./PreferenceCardDesigns";
import R19Designs from "./R19Designs";
import NaomiLuxePlanService from "./NaomiLuxePlanService";
import {
  ArrowLeft, Bell, Calendar as CalendarIcon, Check, ChevronDown, ChevronRight, Clock,
  Copy, Download, Edit, Eye, Folder, Grid as GridIcon, Heart, Home, Image as ImageIcon,
  Instagram, Lock, MapPin, MoreVertical, Plus, RefreshCw, Save, Search, Settings,
  Share2, Sparkles, Star, Sun, Tag as TagIcon, Trash2, Upload, User, Users, Video,
  X, Zap, ArrowUpRight, ToggleLeft, ToggleRight
} from "lucide-react";

const FRAME = "max-w-[420px] mx-auto bg-white min-h-screen shadow-xl";
const NAV_PURPLE = "#7c3aed";

const Header = ({ title, back = "/me", right }) => (
  <header className="sticky top-0 z-20 bg-white border-b border-stone-200 px-4 h-14 flex items-center justify-between">
    <Link to={back} className="w-8 h-8 -ml-1 flex items-center justify-center text-stone-700 hover:bg-stone-100 rounded-full" data-testid="design-header-back">
      <ArrowLeft className="w-4 h-4" />
    </Link>
    <h1 className="text-sm font-bold text-stone-900 uppercase tracking-wider">{title}</h1>
    <div className="w-8 h-8 flex items-center justify-center">{right || null}</div>
  </header>
);

const StateSwitcher = ({ flow, states, current }) => (
  <div className="bg-stone-100 border-t border-stone-200 px-3 py-2 flex flex-wrap gap-1 sticky bottom-0">
    <span className="text-[9px] uppercase tracking-wider text-stone-500 font-bold mr-2 self-center">States</span>
    {states.map((s) => (
      <Link key={s.id} to={`/design/${flow}?s=${s.id}`}
        className={`text-[10px] px-2 py-1 rounded-full ${current === s.id ? "bg-stone-900 text-white" : "bg-white border border-stone-200 text-stone-600"}`}
        data-testid={`state-${flow}-${s.id}`}>
        {s.label}
      </Link>
    ))}
  </div>
);

// ============================================================================
// 1) ROUTINES - inside Experiences tab. Toggle Routines / Calendar.
// ============================================================================
const ROUTINE_STATES = [
  { id: "calendar-default", label: "Calendar (default)" },
  { id: "routines-empty", label: "Routines empty" },
  { id: "routines-list", label: "Routines saved" },
  { id: "setup-type", label: "Setup: pick type" },
  { id: "setup-diy-skincare", label: "Setup: DIY skincare" },
  { id: "setup-service-fill-in", label: "Setup: service (fill-in)" },
  { id: "setup-cadence", label: "Cadence + reminders" },
  { id: "setup-saved", label: "Saved confirmation" },
];

function RoutinesDesign() {
  const [params] = useSearchParams();
  const s = params.get("s") || "calendar-default";
  return (
    <div className={FRAME}>
      <Header title="Experiences" back="/me" right={<Bell className="w-4 h-4 text-stone-500" />} />

      {/* Routines / Calendar toggle - persists across all states */}
      <div className="px-4 pt-3 pb-2">
        <div className="bg-stone-100 p-1 rounded-full flex" data-testid="routines-calendar-toggle">
          <Link to="/design/routines?s=calendar-default" className={`flex-1 text-center text-xs py-2 rounded-full ${s === "calendar-default" ? "bg-white shadow text-stone-900 font-semibold" : "text-stone-500"}`} data-testid="toggle-calendar">
            <CalendarIcon className="w-3 h-3 inline mr-1.5" /> Calendar
          </Link>
          <Link to="/design/routines?s=routines-empty" className={`flex-1 text-center text-xs py-2 rounded-full ${s !== "calendar-default" ? "bg-white shadow text-stone-900 font-semibold" : "text-stone-500"}`} data-testid="toggle-routines">
            <RefreshCw className="w-3 h-3 inline mr-1.5" /> Routines
          </Link>
        </div>
      </div>

      {/* State content */}
      <div className="px-4 pb-32 space-y-4">
        {s === "calendar-default" && <CalendarView />}
        {s === "routines-empty" && <RoutinesEmpty />}
        {s === "routines-list" && <RoutinesList />}
        {s === "setup-type" && <SetupType />}
        {s === "setup-diy-skincare" && <SetupDiySkincare />}
        {s === "setup-service-fill-in" && <SetupServiceFillIn />}
        {s === "setup-cadence" && <SetupCadence />}
        {s === "setup-saved" && <SetupSaved />}
      </div>

      <StateSwitcher flow="routines" states={ROUTINE_STATES} current={s} />
    </div>
  );
}

const CalendarView = () => (
  <div className="space-y-3" data-testid="state-calendar-view">
    <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">June 2026</p>
    <div className="grid grid-cols-7 gap-1 text-center">
      {["S","M","T","W","T","F","S"].map((d, i) => <p key={i} className="text-[9px] text-stone-400 py-1">{d}</p>)}
      {Array.from({ length: 30 }).map((_, i) => {
        const day = i + 1;
        const hasEvent = [3, 12, 15, 22, 28].includes(day);
        return (
          <div key={i} className={`aspect-square flex flex-col items-center justify-center text-[10px] rounded-md ${day === 15 ? "bg-purple-600 text-white font-bold" : hasEvent ? "bg-purple-50 text-purple-900" : "text-stone-600"}`}>
            <span>{day}</span>
            {hasEvent && day !== 15 && <span className="w-1 h-1 rounded-full bg-purple-500 mt-0.5" />}
          </div>
        );
      })}
    </div>
    <div className="space-y-2 pt-3">
      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Upcoming</p>
      <ExpRow title="Sister's wedding glam" date="Jun 15 - 10:30 AM" tag="Makeup" />
      <ExpRow title="Hair Wash + Style" date="Jun 22 - 9:00 AM" tag="Hair" tone="alt" />
      <ExpRow title="Lash fill-in" date="Jun 28 - 2:00 PM" tag="Lash" />
    </div>
  </div>
);
const ExpRow = ({ title, date, tag, tone }) => (
  <div className={`rounded-xl border p-3 flex items-center gap-3 ${tone === "alt" ? "border-dashed border-stone-300 bg-stone-50/50 opacity-70" : "border-stone-200 bg-white"}`}>
    <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center"><Sparkles className="w-4 h-4 text-purple-700" /></div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-semibold text-stone-900 truncate">{title}</p>
      <p className="text-[10px] text-stone-500">{date}</p>
    </div>
    <span className="text-[9px] uppercase tracking-wider text-stone-500 bg-stone-100 px-2 py-0.5 rounded-full">{tag}</span>
  </div>
);

const RoutinesEmpty = () => (
  <div className="text-center py-16 space-y-4" data-testid="state-routines-empty">
    <div className="w-16 h-16 rounded-full bg-purple-100 mx-auto flex items-center justify-center"><RefreshCw className="w-7 h-7 text-purple-600" /></div>
    <div>
      <h3 className="text-lg font-bold text-stone-900">Build your first routine</h3>
      <p className="text-xs text-stone-500 mt-1 max-w-xs mx-auto">Wash days, fill-ins, monthly facials. Recurring care that runs in the background.</p>
    </div>
    <Link to="/design/routines?s=setup-type" className="inline-flex items-center gap-1.5 bg-stone-900 text-white text-xs font-semibold px-5 py-2.5 rounded-full" data-testid="cta-add-first-routine">
      <Plus className="w-3.5 h-3.5" /> Add routine
    </Link>
  </div>
);

const RoutinesList = () => (
  <div className="space-y-3" data-testid="state-routines-list">
    <div className="flex items-center justify-between">
      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Your routines</p>
      <Link to="/design/routines?s=setup-type" className="text-[11px] text-purple-700 font-semibold flex items-center gap-1" data-testid="cta-add-routine"><Plus className="w-3 h-3" /> Add routine</Link>
    </div>
    <RoutineCard icon={Sun} title="Morning skincare" cadence="Every day - 7:00 AM" items="Cleanser - Vitamin C - SPF" tone="amber" />
    <RoutineCard icon={Sparkles} title="Hair wash day" cadence="Every Sunday - 6:00 PM" items="Pre-shampoo oil - Cowash - Leave-in" tone="indigo" />
    <RoutineCard icon={Heart} title="Lash fill-in" cadence="Every 3 weeks - linked to Glow Studio" items="Provider: Mia Kim" tone="rose" linked />
    <RoutineCard icon={Star} title="Monthly facial" cadence="Every 28 days" items="DIY at home" tone="emerald" />
  </div>
);
const RoutineCard = ({ icon: Icon, title, cadence, items, tone, linked }) => (
  <div className="rounded-2xl bg-white border border-stone-200 p-3.5 space-y-2.5">
    <div className="flex items-start gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-${tone}-100 text-${tone}-700`}><Icon className="w-5 h-5" /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-bold text-stone-900 truncate">{title}</p>
          {linked && <span className="text-[8px] uppercase tracking-wider bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-semibold">Linked</span>}
        </div>
        <p className="text-[10px] text-stone-500 mt-0.5">{cadence}</p>
      </div>
      <button className="text-stone-400"><MoreVertical className="w-3.5 h-3.5" /></button>
    </div>
    <p className="text-[11px] text-stone-600 leading-snug">{items}</p>
    <div className="flex gap-1.5">
      <button className="flex-1 text-[10px] py-1.5 rounded-full bg-stone-100 text-stone-700 font-medium">View</button>
      <button className="flex-1 text-[10px] py-1.5 rounded-full bg-stone-100 text-stone-700 font-medium">Skip today</button>
    </div>
  </div>
);

const SetupType = () => (
  <div className="space-y-4" data-testid="state-setup-type">
    <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">New routine - 1/3</p>
    <h2 className="text-lg font-bold text-stone-900">What kind of routine?</h2>
    <div className="space-y-3">
      <Link to="/design/routines?s=setup-diy-skincare" className="block rounded-2xl border border-stone-200 p-4 hover:border-stone-400 bg-white" data-testid="type-diy">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center"><Home className="w-5 h-5 text-emerald-700" /></div>
          <div className="flex-1">
            <p className="font-bold text-sm text-stone-900">DIY - I do this myself</p>
            <p className="text-[11px] text-stone-500 mt-1">Skincare, shave/wax, hair wash, monthly facial, anything you do at home.</p>
            <div className="flex flex-wrap gap-1 mt-2">
              {["Skincare", "Shave/wax", "Hair wash", "Body care", "Hair masking"].map(t => <span key={t} className="text-[9px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full">{t}</span>)}
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-stone-400" />
        </div>
      </Link>
      <Link to="/design/routines?s=setup-service-fill-in" className="block rounded-2xl border border-stone-200 p-4 hover:border-stone-400 bg-white" data-testid="type-service-linked">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center"><Users className="w-5 h-5 text-rose-700" /></div>
          <div className="flex-1">
            <p className="font-bold text-sm text-stone-900">Service - linked to a host</p>
            <p className="text-[11px] text-stone-500 mt-1">Fill-ins, touch-ups, upkeep services with the same provider every time.</p>
            <div className="flex flex-wrap gap-1 mt-2">
              {["Lash fill-in", "Hair upkeep", "Brow tint", "Nail set", "Waxing"].map(t => <span key={t} className="text-[9px] bg-rose-50 text-rose-700 px-1.5 py-0.5 rounded-full">{t}</span>)}
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-stone-400" />
        </div>
      </Link>
      <Link to="/design/routines?s=setup-service-fill-in" className="block rounded-2xl border border-stone-200 p-4 hover:border-stone-400 bg-white" data-testid="type-prep">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center"><Zap className="w-5 h-5 text-indigo-700" /></div>
          <div className="flex-1">
            <p className="font-bold text-sm text-stone-900">Prep for a service</p>
            <p className="text-[11px] text-stone-500 mt-1">Wash hair before a braid appointment, exfoliate before waxing, etc.</p>
          </div>
          <ChevronRight className="w-4 h-4 text-stone-400" />
        </div>
      </Link>
    </div>
  </div>
);

const SetupDiySkincare = () => (
  <div className="space-y-4" data-testid="state-setup-diy-skincare">
    <div className="flex items-center justify-between">
      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">DIY skincare - 2/3</p>
      <Link to="/design/routines?s=setup-type" className="text-[10px] text-stone-500">Change type</Link>
    </div>
    <FormField label="Routine name" testid="routine-name">
      <input defaultValue="Morning skincare" className="w-full px-3 py-2 border border-stone-200 rounded-lg text-xs" />
    </FormField>
    <FormField label="Sub-type" testid="routine-subtype">
      <div className="grid grid-cols-3 gap-1.5">
        {["Skincare","Shave/Wax","Hair wash","Body care","Hair mask","Other"].map((t, i) =>
          <button key={t} className={`text-[10px] py-2 rounded-lg border ${i === 0 ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-200 text-stone-700"}`}>{t}</button>
        )}
      </div>
    </FormField>
    <FormField label="Default time" testid="routine-time">
      <div className="flex items-center gap-2 px-3 py-2 border border-stone-200 rounded-lg">
        <Clock className="w-3.5 h-3.5 text-stone-400" />
        <input type="time" defaultValue="07:00" className="flex-1 text-xs" />
      </div>
    </FormField>
    <FormField label="Default location (optional)" testid="routine-location">
      <div className="flex items-center gap-2 px-3 py-2 border border-stone-200 rounded-lg">
        <MapPin className="w-3.5 h-3.5 text-stone-400" />
        <input defaultValue="Home" className="flex-1 text-xs" />
      </div>
    </FormField>
    <FormField label="Products in this routine" testid="routine-products">
      <div className="space-y-1.5">
        {["CeraVe Hydrating Cleanser","SkinCeuticals C E Ferulic","La Roche-Posay Anthelios SPF 50"].map(p =>
          <div key={p} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-stone-50 border border-stone-100">
            <div className="w-7 h-7 rounded bg-stone-200" />
            <p className="flex-1 text-[11px] font-medium text-stone-800">{p}</p>
            <X className="w-3 h-3 text-stone-400" />
          </div>
        )}
        <button className="w-full text-[10px] py-2 rounded-lg border border-dashed border-stone-300 text-stone-600">+ Add product</button>
      </div>
    </FormField>
    <NextButton to="/design/routines?s=setup-cadence" />
  </div>
);

const SetupServiceFillIn = () => (
  <div className="space-y-4" data-testid="state-setup-service-fill-in">
    <div className="flex items-center justify-between">
      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Service routine - 2/3</p>
      <Link to="/design/routines?s=setup-type" className="text-[10px] text-stone-500">Change type</Link>
    </div>
    <FormField label="Routine name" testid="service-routine-name">
      <input defaultValue="Lash fill-in" className="w-full px-3 py-2 border border-stone-200 rounded-lg text-xs" />
    </FormField>
    <FormField label="Service category" testid="service-routine-category">
      <div className="flex flex-wrap gap-1.5">
        {["Lash","Hair upkeep","Brows","Nails","Waxing","Hair color","Other"].map((t, i) =>
          <button key={t} className={`text-[10px] px-3 py-1.5 rounded-full border ${i === 0 ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-200 text-stone-700"}`}>{t}</button>
        )}
      </div>
    </FormField>
    <FormField label="Default provider" testid="service-routine-provider">
      <div className="rounded-xl border border-stone-200 p-3 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-rose-100" />
        <div className="flex-1">
          <p className="text-xs font-semibold text-stone-900">Mia Kim</p>
          <p className="text-[10px] text-stone-500">Glow Lash Studio - West Hollywood</p>
        </div>
        <button className="text-[10px] text-purple-700 font-semibold">Change</button>
      </div>
    </FormField>
    <FormField label="Default service" testid="service-routine-service">
      <div className="rounded-xl border border-stone-200 p-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-stone-900">Classic Lash Fill</p>
            <p className="text-[10px] text-stone-500">45 min - $85</p>
          </div>
          <button className="text-[10px] text-purple-700 font-semibold">Change</button>
        </div>
      </div>
    </FormField>
    <FormField label="Auto-book or remind only?" testid="service-routine-booking">
      <div className="flex gap-1.5">
        <label className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-stone-900 bg-stone-900 text-white text-[11px] cursor-pointer">
          <input type="radio" name="book" defaultChecked className="hidden" />Remind me to book
        </label>
        <label className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-stone-200 bg-white text-stone-700 text-[11px] cursor-pointer">
          <input type="radio" name="book" className="hidden" />Auto-book
        </label>
      </div>
    </FormField>
    <NextButton to="/design/routines?s=setup-cadence" />
  </div>
);

const SetupCadence = () => (
  <div className="space-y-4" data-testid="state-setup-cadence">
    <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Cadence + reminders - 3/3</p>
    <FormField label="Repeat" testid="cadence-repeat">
      <select className="w-full px-3 py-2 border border-stone-200 rounded-lg text-xs bg-white" defaultValue="weekly">
        <option value="daily">Daily</option>
        <option value="weekly">Weekly</option>
        <option value="biweekly">Every 2 weeks</option>
        <option value="monthly">Monthly</option>
        <option value="custom">Custom interval</option>
      </select>
    </FormField>
    <FormField label="Days of week" testid="cadence-days">
      <div className="flex gap-1">
        {["S","M","T","W","T","F","S"].map((d, i) =>
          <button key={i} className={`flex-1 py-2 rounded-lg text-[11px] font-semibold ${[1,3,5].includes(i) ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-500"}`}>{d}</button>
        )}
      </div>
    </FormField>
    <FormField label="Reminders" testid="cadence-reminders">
      <div className="space-y-2">
        {["The night before - 8:00 PM","Morning of - 30 min before","Day-after recap"].map((r, i) =>
          <label key={r} className="flex items-center gap-2 px-3 py-2 border border-stone-200 rounded-lg cursor-pointer">
            <input type="checkbox" defaultChecked={i < 2} className="accent-stone-900" />
            <span className="text-[11px] text-stone-800">{r}</span>
          </label>
        )}
      </div>
    </FormField>
    <FormField label="End date (optional)" testid="cadence-end-date">
      <div className="flex items-center gap-2 px-3 py-2 border border-stone-200 rounded-lg">
        <CalendarIcon className="w-3.5 h-3.5 text-stone-400" />
        <span className="flex-1 text-xs text-stone-500">No end date</span>
        <button className="text-[10px] text-purple-700 font-semibold">Set</button>
      </div>
    </FormField>
    <div className="flex gap-2 pt-2">
      <Link to="/design/routines?s=setup-diy-skincare" className="flex-1 py-2.5 rounded-full border border-stone-200 text-xs font-medium text-center" data-testid="cadence-back">Back</Link>
      <Link to="/design/routines?s=setup-saved" className="flex-1 py-2.5 rounded-full bg-stone-900 text-white text-xs font-semibold text-center" data-testid="cadence-save">Save routine</Link>
    </div>
  </div>
);

const SetupSaved = () => (
  <div className="text-center py-12 space-y-4" data-testid="state-setup-saved">
    <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center"><Check className="w-7 h-7 text-emerald-600" /></div>
    <div>
      <h3 className="text-lg font-bold text-stone-900">Routine saved</h3>
      <p className="text-xs text-stone-500 mt-1 max-w-xs mx-auto">Morning skincare - every Mon/Wed/Fri at 7:00 AM. We&apos;ll send reminders the night before and 30 min ahead.</p>
    </div>
    <div className="space-y-2 max-w-xs mx-auto">
      <Link to="/design/routines?s=routines-list" className="block w-full py-2.5 rounded-full bg-stone-900 text-white text-xs font-semibold" data-testid="saved-view-all">View all routines</Link>
      <button className="block w-full py-2.5 rounded-full border border-stone-200 text-xs font-medium text-stone-700" data-testid="saved-duplicate">Duplicate this routine</button>
      <Link to="/design/routines?s=setup-type" className="block w-full py-2.5 text-xs font-medium text-stone-600" data-testid="saved-add-another">+ Add another</Link>
    </div>
  </div>
);

const FormField = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-1.5">{label}</p>
    {children}
  </div>
);
const NextButton = ({ to }) => (
  <Link to={to} className="block text-center w-full py-2.5 rounded-full bg-stone-900 text-white text-xs font-semibold mt-2" data-testid="design-next-btn">Continue</Link>
);

// ============================================================================
// 2) INSPO - folders, subfolders, IG/TikTok integrations
// ============================================================================
const INSPO_STATES = [
  { id: "categories", label: "Categories root" },
  { id: "category-makeup", label: "Inside Makeup" },
  { id: "subfolder-bridal", label: "Inside subfolder" },
  { id: "upload-options", label: "Upload options" },
  { id: "ig-tiktok-pull", label: "IG/TikTok pull" },
];

function InspoDesign() {
  const [params] = useSearchParams();
  const s = params.get("s") || "categories";
  return (
    <div className={FRAME}>
      <Header title="Inspo" right={<button className="text-stone-700" data-testid="inspo-search"><Search className="w-4 h-4" /></button>} />
      <div className="px-4 pb-32 pt-3 space-y-4">
        {s === "categories" && <InspoCategories />}
        {s === "category-makeup" && <InspoCategoryMakeup />}
        {s === "subfolder-bridal" && <InspoSubfolderBridal />}
        {s === "upload-options" && <InspoUploadOptions />}
        {s === "ig-tiktok-pull" && <InspoIgTikTokPull />}
      </div>
      <StateSwitcher flow="inspo" states={INSPO_STATES} current={s} />
    </div>
  );
}
const InspoCategories = () => (
  <div className="space-y-3" data-testid="state-inspo-categories">
    <div className="flex items-center justify-between">
      <p className="text-xs text-stone-500">Folders by category</p>
      <Link to="/design/inspo?s=upload-options" className="text-[11px] text-purple-700 font-semibold flex items-center gap-1" data-testid="inspo-add-cta"><Plus className="w-3 h-3" /> Add</Link>
    </div>
    <div className="grid grid-cols-2 gap-3">
      {[
        { name: "Makeup", count: 48, slug: "category-makeup", color: "bg-rose-100", img: "https://images.unsplash.com/photo-1599733589046-77fde6f1f5b6?w=300" },
        { name: "Hair", count: 22, slug: "categories", color: "bg-amber-100", img: "https://images.unsplash.com/photo-1605497788044-5a32c7078486?w=300" },
        { name: "Nails", count: 15, slug: "categories", color: "bg-violet-100", img: "https://images.unsplash.com/photo-1604654894611-6973b376cbde?w=300" },
        { name: "Lashes", count: 9, slug: "categories", color: "bg-pink-100", img: "https://images.unsplash.com/photo-1583241800698-9c2e89f1d2f4?w=300" },
        { name: "Wellness", count: 12, slug: "categories", color: "bg-emerald-100", img: "https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=300" },
        { name: "Style", count: 31, slug: "categories", color: "bg-indigo-100", img: "https://images.unsplash.com/photo-1483985988355-763728e1935b?w=300" },
      ].map(c => (
        <Link key={c.name} to={`/design/inspo?s=${c.slug}`} className="rounded-2xl overflow-hidden border border-stone-200 bg-white" data-testid={`inspo-folder-${c.name.toLowerCase()}`}>
          <div className="aspect-square bg-cover bg-center" style={{ backgroundImage: `url(${c.img})` }} />
          <div className="p-2.5">
            <p className="text-xs font-bold text-stone-900">{c.name}</p>
            <p className="text-[10px] text-stone-500">{c.count} pins - 3 folders</p>
          </div>
        </Link>
      ))}
    </div>
    <div className="pt-2">
      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-2">Phygital shared folder</p>
      <Link to="/design/inspo?s=subfolder-bridal" className="block rounded-2xl border border-purple-200 bg-purple-50 p-3" data-testid="inspo-phygital-shared">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-purple-200 flex items-center justify-center"><Sparkles className="w-5 h-5 text-purple-700" /></div>
          <div className="flex-1">
            <p className="text-xs font-bold text-stone-900">Experiences</p>
            <p className="text-[10px] text-purple-700">Hosts + clients share final looks here</p>
          </div>
          <ChevronRight className="w-4 h-4 text-purple-500" />
        </div>
      </Link>
    </div>
  </div>
);

const InspoCategoryMakeup = () => (
  <div className="space-y-3" data-testid="state-inspo-category-makeup">
    <div className="flex items-center justify-between">
      <Link to="/design/inspo?s=categories" className="text-[11px] text-stone-500 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> All categories</Link>
      <Link to="/design/inspo?s=upload-options" className="text-[11px] text-purple-700 font-semibold flex items-center gap-1" data-testid="makeup-new-folder"><Plus className="w-3 h-3" /> New folder</Link>
    </div>
    <div>
      <h3 className="text-lg font-bold text-stone-900">Makeup</h3>
      <p className="text-[11px] text-stone-500">48 pins across 4 folders</p>
    </div>
    <div className="grid grid-cols-2 gap-3">
      {[
        { name: "Bridal", count: 14, type: "user", slug: "subfolder-bridal" },
        { name: "Soft glam", count: 22, type: "user", slug: "categories" },
        { name: "Editorial", count: 9, type: "user", slug: "categories" },
        { name: "Experiences shared", count: 3, type: "phygital", slug: "subfolder-bridal" },
      ].map(f => (
        <Link key={f.name} to={`/design/inspo?s=${f.slug}`} className={`rounded-2xl overflow-hidden border p-3 ${f.type === "phygital" ? "border-purple-200 bg-purple-50" : "border-stone-200 bg-white"}`} data-testid={`inspo-subfolder-${f.name.toLowerCase().replace(/ /g,'-')}`}>
          <div className="grid grid-cols-3 gap-px aspect-[3/2] mb-2 rounded-md overflow-hidden">
            {[0,1,2].map(i => <div key={i} className="bg-stone-200" />)}
          </div>
          <p className="text-xs font-bold text-stone-900">{f.name}</p>
          <p className="text-[10px] text-stone-500">{f.count} pins {f.type === "phygital" && "- shared"}</p>
        </Link>
      ))}
    </div>
  </div>
);

const InspoSubfolderBridal = () => (
  <div className="space-y-3" data-testid="state-inspo-subfolder-bridal">
    <div className="flex items-center justify-between">
      <Link to="/design/inspo?s=category-makeup" className="text-[11px] text-stone-500 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Makeup</Link>
      <div className="flex gap-2 text-stone-400">
        <button><Share2 className="w-3.5 h-3.5" /></button>
        <button><MoreVertical className="w-3.5 h-3.5" /></button>
      </div>
    </div>
    <div>
      <h3 className="text-lg font-bold text-stone-900">Bridal</h3>
      <p className="text-[11px] text-stone-500">14 pins - 3 sources</p>
    </div>
    <div className="flex gap-1.5 text-[10px]">
      {["All 14","Uploaded 6","Pinterest 4","Instagram 3","TikTok 1"].map((t,i) =>
        <button key={t} className={`px-2.5 py-1 rounded-full whitespace-nowrap ${i === 0 ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}>{t}</button>
      )}
    </div>
    <div className="grid grid-cols-3 gap-1.5">
      {Array.from({length: 12}).map((_,i) => (
        <div key={i} className="aspect-square bg-stone-200 rounded-md relative overflow-hidden" data-testid={`inspo-pin-${i}`}>
          <div className="absolute inset-0 bg-gradient-to-br from-rose-200 to-pink-300" />
          {i % 4 === 0 && <Instagram className="absolute top-1 right-1 w-3 h-3 text-white drop-shadow" />}
          {i % 5 === 0 && <Video className="absolute top-1 right-1 w-3 h-3 text-white drop-shadow" />}
        </div>
      ))}
    </div>
    <Link to="/design/inspo?s=upload-options" className="flex items-center justify-center gap-1.5 py-2.5 rounded-full border border-dashed border-stone-300 text-stone-600 text-xs font-semibold" data-testid="bridal-add-pin">
      <Plus className="w-3 h-3" /> Add to Bridal
    </Link>
  </div>
);

const InspoUploadOptions = () => (
  <div className="space-y-4" data-testid="state-inspo-upload-options">
    <Link to="/design/inspo?s=subfolder-bridal" className="text-[11px] text-stone-500 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back</Link>
    <div>
      <h3 className="text-lg font-bold text-stone-900">Add inspo to Bridal</h3>
      <p className="text-[11px] text-stone-500">Pick a source. We&apos;ll save to your folder.</p>
    </div>
    <button className="w-full rounded-2xl border-2 border-dashed border-stone-300 p-5 flex items-center gap-3 hover:border-stone-500" data-testid="upload-from-device">
      <div className="w-12 h-12 rounded-xl bg-stone-100 flex items-center justify-center"><Upload className="w-5 h-5 text-stone-700" /></div>
      <div className="flex-1 text-left">
        <p className="font-bold text-sm text-stone-900">Upload from device</p>
        <p className="text-[10px] text-stone-500">JPG / PNG / HEIC, up to 10 MB each</p>
      </div>
    </button>
    <button className="w-full rounded-2xl border border-stone-200 p-4 flex items-center gap-3" data-testid="upload-pinterest">
      <div className="w-11 h-11 rounded-xl bg-red-100 flex items-center justify-center text-red-600 font-bold text-lg">P</div>
      <div className="flex-1 text-left">
        <p className="font-bold text-sm text-stone-900">Pull from Pinterest</p>
        <p className="text-[10px] text-stone-500">Connect a board or paste a pin URL</p>
      </div>
      <ArrowUpRight className="w-3.5 h-3.5 text-stone-400" />
    </button>
    <button className="w-full rounded-2xl border border-stone-200 p-4 flex items-center gap-3" data-testid="upload-instagram">
      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center"><Instagram className="w-5 h-5 text-white" /></div>
      <div className="flex-1 text-left">
        <p className="font-bold text-sm text-stone-900">Instagram saves</p>
        <p className="text-[10px] text-stone-500">Pull from your saved collections</p>
      </div>
      <span className="text-[8px] uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold">Soon</span>
    </button>
    <button className="w-full rounded-2xl border border-stone-200 p-4 flex items-center gap-3" data-testid="upload-tiktok">
      <div className="w-11 h-11 rounded-xl bg-stone-900 flex items-center justify-center"><Video className="w-5 h-5 text-white" /></div>
      <div className="flex-1 text-left">
        <p className="font-bold text-sm text-stone-900">TikTok favorites</p>
        <p className="text-[10px] text-stone-500">DM yourself a TikTok - it&apos;ll auto-sort</p>
      </div>
      <span className="text-[8px] uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold">Soon</span>
    </button>
    <Link to="/design/inspo?s=ig-tiktok-pull" className="block text-center text-[11px] text-purple-700 font-semibold pt-1">See IG/TikTok preview design -</Link>
  </div>
);

const InspoIgTikTokPull = () => (
  <div className="space-y-4" data-testid="state-inspo-ig-tiktok-pull">
    <Link to="/design/inspo?s=upload-options" className="text-[11px] text-stone-500 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back</Link>
    <div>
      <h3 className="text-lg font-bold text-stone-900">Connected feeds</h3>
      <p className="text-[11px] text-stone-500">Folders the team can test with pilots - share-to-DM rolls out next.</p>
    </div>
    {/* Instagram block */}
    <div className="rounded-2xl border border-stone-200 overflow-hidden">
      <div className="px-3 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white flex items-center justify-between">
        <div className="flex items-center gap-2"><Instagram className="w-4 h-4" /><p className="text-xs font-bold">Instagram - @aria.j</p></div>
        <span className="text-[9px] bg-white/20 px-2 py-0.5 rounded-full">3 folders synced</span>
      </div>
      <div className="p-3 grid grid-cols-3 gap-2">
        {[
          { name: "Bridal saves", count: 8 },
          { name: "Soft glam", count: 14 },
          { name: "Hair inspo", count: 22 },
        ].map(f => (
          <div key={f.name} className="space-y-1">
            <div className="aspect-square bg-gradient-to-br from-pink-200 to-purple-200 rounded-md" />
            <p className="text-[10px] font-semibold text-stone-900 leading-tight">{f.name}</p>
            <p className="text-[9px] text-stone-500">{f.count} posts</p>
          </div>
        ))}
      </div>
    </div>
    {/* TikTok block */}
    <div className="rounded-2xl border border-stone-200 overflow-hidden">
      <div className="px-3 py-2.5 bg-stone-900 text-white flex items-center justify-between">
        <div className="flex items-center gap-2"><Video className="w-4 h-4" /><p className="text-xs font-bold">TikTok - @aria.james</p></div>
        <span className="text-[9px] bg-white/20 px-2 py-0.5 rounded-full">2 folders synced</span>
      </div>
      <div className="p-3 grid grid-cols-3 gap-2">
        {[
          { name: "Hair tutorials", count: 15 },
          { name: "Skincare routines", count: 9 },
        ].map(f => (
          <div key={f.name} className="space-y-1">
            <div className="aspect-square bg-gradient-to-br from-stone-700 to-stone-900 rounded-md flex items-center justify-center"><Video className="w-4 h-4 text-white/50" /></div>
            <p className="text-[10px] font-semibold text-stone-900 leading-tight">{f.name}</p>
            <p className="text-[9px] text-stone-500">{f.count} clips</p>
          </div>
        ))}
      </div>
    </div>
    <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3">
      <p className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Coming next</p>
      <p className="text-xs text-stone-700 mt-1">DM your inspo straight to @phygital and it&apos;ll auto-sort into the right folder. Pilots get early access.</p>
    </div>
  </div>
);

// ============================================================================
// 3) PREFERENCES dashboard - universal + beauty
// ============================================================================
const PREFS_STATES = [
  { id: "universal", label: "Universal" },
  { id: "beauty", label: "Beauty" },
  { id: "routine-entry", label: "Routine entry" },
];
function PrefsDesign() {
  const [params] = useSearchParams();
  const s = params.get("s") || "universal";
  return (
    <div className={FRAME}>
      <Header title="Preferences" />
      <div className="px-4 pt-3 pb-32 space-y-4">
        <div className="bg-stone-100 p-1 rounded-full flex" data-testid="prefs-section-toggle">
          <Link to="/design/preferences?s=universal" className={`flex-1 text-center text-xs py-2 rounded-full ${s === "universal" ? "bg-white shadow font-semibold text-stone-900" : "text-stone-500"}`} data-testid="prefs-tab-universal">Universal defaults</Link>
          <Link to="/design/preferences?s=beauty" className={`flex-1 text-center text-xs py-2 rounded-full ${(s === "beauty" || s === "routine-entry") ? "bg-white shadow font-semibold text-stone-900" : "text-stone-500"}`} data-testid="prefs-tab-beauty">Beauty</Link>
        </div>
        {s === "universal" && <PrefsUniversal />}
        {s === "beauty" && <PrefsBeauty />}
        {s === "routine-entry" && <PrefsRoutineEntry />}
      </div>
      <StateSwitcher flow="preferences" states={PREFS_STATES} current={s} />
    </div>
  );
}
const PrefsUniversal = () => (
  <div className="space-y-3" data-testid="state-prefs-universal">
    <PrefSection icon={Sparkles} title="Allergies" subtitle="3 set - peanuts, latex, pollen" tone="red" />
    <PrefSection icon={Heart} title="Accessibility" subtitle="Wheelchair accessible venues - low fragrance" tone="purple" />
    <PrefSection icon={Star} title="Interests" subtitle="Music genres synced with Tap In" tone="indigo" extra={<TapInBadge />} />
    <div className="pt-1">
      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-2">Tap In interest map</p>
      <div className="rounded-2xl border border-indigo-100 bg-indigo-50 p-3 grid grid-cols-2 gap-2">
        {["R&B","Afrobeats","Jazz","House","Yoga","Wellness retreats","Food festivals","Art shows"].map((t,i) =>
          <div key={t} className="flex items-center gap-1.5">
            <Check className="w-3 h-3 text-indigo-600" />
            <span className="text-[11px] text-stone-800">{t}</span>
          </div>
        )}
      </div>
    </div>
  </div>
);
const TapInBadge = () => (
  <span className="text-[8px] uppercase tracking-wider bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full font-bold ml-2">Tap In synced</span>
);
const PrefSection = ({ icon: Icon, title, subtitle, tone, extra }) => (
  <button className="w-full rounded-2xl border border-stone-200 bg-white p-3.5 flex items-center gap-3 text-left">
    <div className={`w-10 h-10 rounded-xl bg-${tone}-100 text-${tone}-700 flex items-center justify-center`}><Icon className="w-5 h-5" /></div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center"><p className="text-sm font-bold text-stone-900">{title}</p>{extra}</div>
      <p className="text-[11px] text-stone-500 truncate">{subtitle}</p>
    </div>
    <ChevronRight className="w-4 h-4 text-stone-400" />
  </button>
);

const PrefsBeauty = () => (
  <div className="space-y-3" data-testid="state-prefs-beauty">
    <PrefSection icon={Sun} title="Skincare" subtitle="Opens routine flow - 3 products linked" tone="amber" />
    <Link to="/design/preferences?s=routine-entry" className="text-[11px] text-purple-700 font-semibold flex items-center gap-1 px-1" data-testid="prefs-skincare-routine"><Plus className="w-3 h-3" /> Add skincare routine</Link>
    <PrefSection icon={Sparkles} title="Makeup" subtitle="Combination skin - warm undertone - 8 products" tone="rose" />
    <PrefSection icon={ImageIcon} title="Hair" subtitle="Type 3B - protective styles preferred" tone="indigo" />
    <PrefSection icon={Heart} title="Nails" subtitle="Almond shape - gel + chrome" tone="pink" />
    <PrefSection icon={Eye} title="Lashes" subtitle="Natural volume - 11mm classic" tone="purple" />
    <PrefSection icon={Star} title="Style" subtitle="Casual + elevated - size M/L" tone="emerald" />
  </div>
);

const PrefsRoutineEntry = () => (
  <div className="space-y-4" data-testid="state-prefs-routine-entry">
    <Link to="/design/preferences?s=beauty" className="text-[11px] text-stone-500 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Beauty</Link>
    <div>
      <h3 className="text-lg font-bold text-stone-900">Add skincare routine</h3>
      <p className="text-[11px] text-stone-500">We&apos;ll save this as a recurring routine + link the products to your preferences.</p>
    </div>
    <FormField label="Routine name" testid="re-name">
      <input defaultValue="Morning skincare" className="w-full px-3 py-2 border border-stone-200 rounded-lg text-xs" />
    </FormField>
    <FormField label="When" testid="re-when">
      <div className="grid grid-cols-2 gap-1.5">
        {["Morning","Night","Both","Custom"].map((t,i) =>
          <button key={t} className={`text-[11px] py-2 rounded-lg border ${i === 0 ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-200 text-stone-700"}`}>{t}</button>
        )}
      </div>
    </FormField>
    <FormField label="Products used" testid="re-products">
      <div className="space-y-1.5">
        {[
          { brand: "CeraVe", name: "Hydrating Cleanser" },
          { brand: "SkinCeuticals", name: "C E Ferulic" },
          { brand: "La Roche-Posay", name: "Anthelios SPF 50" },
        ].map(p => (
          <div key={p.name} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-stone-50 border border-stone-100">
            <div className="w-7 h-7 rounded bg-stone-200" />
            <div className="flex-1 min-w-0">
              <p className="text-[9px] uppercase text-stone-500 tracking-wider">{p.brand}</p>
              <p className="text-[11px] font-medium text-stone-800 truncate">{p.name}</p>
            </div>
            <X className="w-3 h-3 text-stone-400" />
          </div>
        ))}
        <button className="w-full text-[10px] py-2 rounded-lg border border-dashed border-stone-300 text-stone-600">+ Add product</button>
      </div>
    </FormField>
    <Link to="/design/routines?s=setup-cadence" className="block text-center w-full py-2.5 rounded-full bg-stone-900 text-white text-xs font-semibold mt-2" data-testid="re-continue">Continue to cadence</Link>
  </div>
);

// ============================================================================
// 4) NOTIFICATIONS popdown
// ============================================================================
function NotificationsDesign() {
  return (
    <div className={FRAME}>
      <div className="bg-stone-50 min-h-screen relative">
        {/* mock top nav */}
        <div className="bg-white border-b border-stone-200 px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-pink-500" />
            <p className="text-sm font-bold text-stone-900">Phygital</p>
          </div>
          <div className="flex items-center gap-3">
            <button className="relative w-9 h-9 rounded-full bg-stone-900 flex items-center justify-center" data-testid="notif-trigger">
              <Bell className="w-4 h-4 text-white" />
              <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500" />
            </button>
            <div className="w-9 h-9 rounded-full bg-stone-200" />
          </div>
        </div>
        {/* popdown */}
        <div className="absolute right-4 top-16 w-[360px] max-w-[90%] bg-white rounded-2xl shadow-2xl border border-stone-200 overflow-hidden z-30" data-testid="notif-popdown">
          <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between">
            <p className="text-sm font-bold text-stone-900">Notifications</p>
            <button className="text-[10px] text-purple-700 font-semibold" data-testid="notif-mark-all-read">Mark all read</button>
          </div>
          <div className="bg-stone-50 px-4 py-1.5 flex gap-1 text-[10px]">
            {["All 12","Bookings 4","Reminders 5","Hosts 3"].map((t,i) =>
              <button key={t} className={`px-2 py-1 rounded-full ${i === 0 ? "bg-stone-900 text-white" : "text-stone-500"}`}>{t}</button>
            )}
          </div>
          <div className="max-h-[400px] overflow-y-auto divide-y divide-stone-100">
            <Notif tone="rose" icon={Heart} title="Shelsea confirmed your booking" body="Full Glam - Jun 15 at 10:30 AM. Tap to add to calendar." time="2 min ago" unread />
            <Notif tone="amber" icon={Clock} title="Reminder: tomorrow's morning skincare" body="7:00 AM - cleanser, vitamin C, SPF." time="1 hr ago" unread />
            <Notif tone="purple" icon={Sparkles} title="Mia added you to her client directory" body="You can now book lash fill-ins directly." time="3 hr ago" unread />
            <Notif tone="indigo" icon={CalendarIcon} title="Hair Wash routine in 30 min" body="Jun 22 - 9:00 AM." time="6 hr ago" />
            <Notif tone="emerald" icon={Check} title="Final look uploaded to Bridal folder" body="Your makeup look from Sat is in your inspo." time="Yesterday" />
            <Notif tone="rose" icon={Bell} title="Lash fill-in due this week" body="Last fill was 3 weeks ago. Auto-remind on." time="Yesterday" />
          </div>
          <div className="px-4 py-2 border-t border-stone-100 flex items-center justify-between">
            <Link to="/design/settings?s=reminders" className="text-[11px] text-stone-600" data-testid="notif-go-settings">Notification settings</Link>
            <button className="text-[11px] text-stone-600">View all</button>
          </div>
        </div>
      </div>
      <StateSwitcher flow="notifications" states={[{id:"open",label:"Pop-down open"}]} current="open" />
    </div>
  );
}
const Notif = ({ tone, icon: Icon, title, body, time, unread }) => (
  <div className={`px-4 py-3 flex items-start gap-3 ${unread ? "bg-purple-50/40" : "bg-white"}`}>
    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-${tone}-100 text-${tone}-700`}><Icon className="w-3.5 h-3.5" /></div>
    <div className="flex-1 min-w-0">
      <p className="text-[12px] font-semibold text-stone-900 leading-tight">{title}</p>
      <p className="text-[10px] text-stone-500 mt-0.5 leading-snug">{body}</p>
      <p className="text-[9px] text-stone-400 mt-0.5">{time}</p>
    </div>
    {unread && <span className="w-1.5 h-1.5 rounded-full bg-purple-600 mt-1.5" />}
  </div>
);

// ============================================================================
// 5) SETTINGS - reminders + become a host CTA
// ============================================================================
const SETTINGS_STATES = [
  { id: "main", label: "Main" },
  { id: "reminders", label: "Reminders + notifications" },
];
function SettingsDesign() {
  const [params] = useSearchParams();
  const s = params.get("s") || "main";
  return (
    <div className={FRAME}>
      <Header title="Settings" />
      <div className="px-4 pt-3 pb-32 space-y-4">
        {s === "main" && <SettingsMain />}
        {s === "reminders" && <SettingsReminders />}
      </div>
      <StateSwitcher flow="settings" states={SETTINGS_STATES} current={s} />
    </div>
  );
}
const SettingsRow = ({ icon: Icon, title, subtitle, to, tone = "stone", chevron = true, danger }) => (
  <Link to={to || "#"} className={`flex items-center gap-3 px-3.5 py-3 ${danger ? "text-red-600" : "text-stone-900"}`}>
    <div className={`w-8 h-8 rounded-lg bg-${tone}-100 flex items-center justify-center text-${tone}-700`}><Icon className="w-4 h-4" /></div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold leading-tight">{title}</p>
      {subtitle && <p className="text-[11px] text-stone-500 truncate">{subtitle}</p>}
    </div>
    {chevron && <ChevronRight className="w-4 h-4 text-stone-300" />}
  </Link>
);
const SettingsMain = () => (
  <div className="space-y-4" data-testid="state-settings-main">
    {/* Become a host CTA */}
    <div className="rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 p-5 text-white" data-testid="cta-become-host">
      <Sparkles className="w-5 h-5 mb-2" />
      <p className="text-base font-bold leading-tight">Become a host on Phygital</p>
      <p className="text-[11px] text-white/85 mt-1 leading-snug">Open bookings, build your kanban, get your own dashboard. Free for the first 30 days.</p>
      <button className="mt-3 inline-flex items-center gap-1.5 bg-white text-purple-700 text-[11px] font-bold px-4 py-2 rounded-full" data-testid="become-host-cta-btn">
        Start hosting <ArrowUpRight className="w-3 h-3" />
      </button>
    </div>

    <div className="rounded-2xl bg-white border border-stone-200 divide-y divide-stone-100 overflow-hidden">
      <SettingsRow icon={User} title="Account" subtitle="Aria James - aria.james@phygital.app" />
      <SettingsRow icon={Bell} title="Reminders + notifications" subtitle="3 channels on - quiet hours 10p-7a" to="/design/settings?s=reminders" tone="amber" />
      <SettingsRow icon={Lock} title="Privacy + visibility" subtitle="Profile public - card defaults private" tone="purple" />
      <SettingsRow icon={CalendarIcon} title="Calendar integrations" subtitle="Google connected" tone="indigo" />
      <SettingsRow icon={GridIcon} title="Connected accounts" subtitle="Spotify - Tap In - Instagram" tone="rose" />
    </div>

    <div className="rounded-2xl bg-white border border-stone-200 divide-y divide-stone-100 overflow-hidden">
      <SettingsRow icon={Save} title="Saved cards + payment" tone="emerald" />
      <SettingsRow icon={TagIcon} title="Coupons + credits" subtitle="$25 credit available" tone="amber" />
      <SettingsRow icon={Heart} title="Refer a friend" subtitle="Earn $20 per signup" tone="pink" />
    </div>

    <div className="rounded-2xl bg-white border border-stone-200 divide-y divide-stone-100 overflow-hidden">
      <SettingsRow icon={Sparkles} title="Help + support" />
      <SettingsRow icon={Download} title="Export my data" />
      <SettingsRow icon={Trash2} title="Delete account" danger chevron={false} />
    </div>
    <p className="text-[10px] text-stone-400 text-center pt-3">Phygital v9.50 - made with care.</p>
  </div>
);
const SettingsReminders = () => (
  <div className="space-y-4" data-testid="state-settings-reminders">
    <Link to="/design/settings?s=main" className="text-[11px] text-stone-500 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Settings</Link>
    <div>
      <h3 className="text-lg font-bold text-stone-900">Reminders + notifications</h3>
      <p className="text-[11px] text-stone-500">Pick how + when you want to hear from Phygital.</p>
    </div>
    <SectionLabel text="Channels" />
    <div className="rounded-2xl bg-white border border-stone-200 divide-y divide-stone-100 overflow-hidden">
      <ToggleRow icon={Bell} title="Push notifications" on />
      <ToggleRow icon={Heart} title="SMS" subtitle="+1 (555) 555-0103" on />
      <ToggleRow icon={Sparkles} title="Email" subtitle="aria.james@phygital.app" on />
      <ToggleRow icon={Instagram} title="DM via Instagram" subtitle="Coming soon" />
    </div>

    <SectionLabel text="What you&apos;ll hear about" />
    <div className="rounded-2xl bg-white border border-stone-200 divide-y divide-stone-100 overflow-hidden">
      <ToggleRow icon={CalendarIcon} title="Booking updates" subtitle="Confirmations, reschedules, cancellations" on />
      <ToggleRow icon={Clock} title="Routine reminders" subtitle="Night before + 30 min ahead" on />
      <ToggleRow icon={RefreshCw} title="Upkeep nudges" subtitle="Lash fill due, hair wash due, etc." on />
      <ToggleRow icon={Users} title="Host updates" subtitle="When your providers post or change availability" />
      <ToggleRow icon={Sparkles} title="Phygital news" subtitle="Product updates, new features" />
    </div>

    <SectionLabel text="Quiet hours" />
    <div className="rounded-2xl bg-white border border-stone-200 p-3.5 space-y-2.5">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-stone-900">Hush notifications</p>
        <div className="w-9 h-5 rounded-full bg-stone-900 flex items-center justify-end px-0.5"><div className="w-4 h-4 rounded-full bg-white" /></div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-stone-50 border border-stone-200 p-2"><p className="text-[10px] text-stone-500">From</p><p className="text-sm font-bold text-stone-900">10:00 PM</p></div>
        <div className="rounded-lg bg-stone-50 border border-stone-200 p-2"><p className="text-[10px] text-stone-500">Until</p><p className="text-sm font-bold text-stone-900">7:00 AM</p></div>
      </div>
    </div>

    <SectionLabel text="Reminder timing" />
    <div className="rounded-2xl bg-white border border-stone-200 p-3.5 space-y-2">
      {[
        { label: "Day before", val: "Default - 8:00 PM" },
        { label: "Morning of", val: "30 minutes ahead" },
        { label: "Post-service", val: "1 hour after" },
      ].map(r => (
        <div key={r.label} className="flex items-center justify-between">
          <p className="text-xs text-stone-900">{r.label}</p>
          <p className="text-[11px] text-stone-500">{r.val}</p>
        </div>
      ))}
    </div>
  </div>
);
const SectionLabel = ({ text }) => <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold px-1">{text}</p>;
const ToggleRow = ({ icon: Icon, title, subtitle, on }) => (
  <div className="flex items-center gap-3 px-3.5 py-2.5">
    <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center"><Icon className="w-3.5 h-3.5 text-stone-700" /></div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-semibold text-stone-900 leading-tight">{title}</p>
      {subtitle && <p className="text-[10px] text-stone-500 truncate">{subtitle}</p>}
    </div>
    <div className={`w-9 h-5 rounded-full ${on ? "bg-stone-900 justify-end" : "bg-stone-200 justify-start"} flex items-center px-0.5`}>
      <div className="w-4 h-4 rounded-full bg-white" />
    </div>
  </div>
);

// ============================================================================
export default function DesignPreview() {
  const { flow } = useParams();
  if (flow === "routines") return <RoutinesDesign />;
  if (flow === "inspo") return <InspoDesign />;
  if (flow === "preferences") return <PrefsDesign />;
  if (flow === "notifications") return <NotificationsDesign />;
  if (flow === "settings") return <SettingsDesign />;
  // R18 - preference card final look + visibility redesign flows
  if (["preference-card","final-look","visibility","media-library"].includes(flow)) {
    return <PreferenceCardDesigns flow={flow} />;
  }
  // R19 - inspo v2 / confirmation v2 / preference label reference / host insights
  if (["inspo-v2","confirmation-v2","pref-labels","host-insights"].includes(flow)) {
    return <R19Designs flow={flow} />;
  }
  // R21 v2 - Naomi Luxe "Plan a Service" clickable mockup (single-file, ?s=<state>)
  if (flow === "naomi-luxe") {
    return <NaomiLuxePlanService />;
  }
  return (
    <div className={FRAME + " p-6"}>
      <h1 className="text-lg font-bold mb-3">Design previews</h1>
      <p className="text-xs text-stone-500 mb-4">Static screens for upcoming features.</p>
      <ul className="space-y-2">
        {["routines","inspo","preferences","notifications","settings"].map(f =>
          <li key={f}><Link to={`/design/${f}`} className="text-purple-700 underline text-xs">/design/{f}</Link></li>
        )}
      </ul>
    </div>
  );
}
